import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

export const shortRelativeTime = (
  date: string | Date | number,
  locale: string = "en"
): string => {
  const now = dayjs();
  const target = dayjs(date);
  const diff = now.diff(target);

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  const isFrench = locale.startsWith("fr");

  if (years > 0)
    return isFrench ? `${years} ${years === 1 ? "an" : "ans"}` : `${years} yr`;
  if (months > 0) return isFrench ? `${months} mois` : `${months} mo`;
  if (days > 0)
    return isFrench
      ? `${days} ${days === 1 ? "jour" : "jours"}`
      : `${days} ${days === 1 ? "day" : "days"}`;
  if (hours > 0) return isFrench ? `${hours} h` : `${hours} hr`;
  if (minutes > 0) return isFrench ? `${minutes} min` : `${minutes} min`;
  return isFrench ? "à l'instant" : "just now";
};

export { dayjs };

export const toUTCString = (date: string, format = "MM/DD/YYYY") => {
  return dayjs.utc(date).format(format);
};

export const toUTCDate = (date: Date | string | undefined) => {
  if (!date) return undefined;
  return new Date(dayjs(date).utc().format("YYYY-MM-DD") + "T00:00:00");
};

/**
 * Formats a date-only value (like dateOfBirth or dateOfLoss) without timezone conversion.
 * This function treats the input as a pure date (YYYY-MM-DD) and formats it directly
 * without interpreting it as a timestamp in any timezone.
 *
 * @param date - Date string in YYYY-MM-DD format or ISO format
 * @param format - Output format (default: MM/DD/YYYY)
 * @returns Formatted date string
 */
export const formatDateOnly = (
  date: string | undefined | null,
  format = "MM/DD/YYYY"
): string => {
  if (!date) return "-";

  const dateOnly = date.split("T")[0];

  const [year, month, day] = dateOnly.split("-").map(Number);

  const localDate = new Date(year, month - 1, day, 12, 0, 0);

  return dayjs(localDate).format(format);
};
